import {
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	IconButton,
	Paper,
	Stack,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "@mui/material";
import { merchantTableColumns, mearchatSkuDummyData } from "./data";
import { useEffect, useRef, useState } from "react";
import { converAttrToDatagridCol, createdByColumns, selectOptionsKeyName, statusOptions } from "data/globalData";
import { AuthState } from "context/AuthContext";
import { GlobalState } from "context/GlobalContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import axios from "axios";
import { CameraAltRounded, ClearRounded, FileUploadOutlined, Image as ImageIcon } from "@mui/icons-material";
import DatagridWithApiPagination from "components/ReusableComponents/DatagridWithApiPagination/DatagridWithApiPagination";
import SkuSectionTanstack from "../CreateShoot/SkuSection/SkuSectionTanstack";
import {
	sampleStatusColumn,
	shootStudioSampleDetailsColumns,
	shootSkuIcodeColumn,
	shootStudioSampleGrnImgUrlCol,
	shootStudioSampleGrnUploadedCol,
	shootStudioSampleImgUrlCol,
	hasSampleGrnImage,
} from "../ShootsDashboard/data";
import { set } from "lodash";
import RenderInputField from "components/PimDashboard/CommonComponents/RenderInputField/RenderInputField";
import { grey } from "@mui/material/colors";
import SampleHistoryModal from "../MerchantData/SampleHistoryModal/SampleHistoryModal";
import CameraCapture from "./CameraCapture/CameraCapture";
import GinesysCodeUploadModal from "./GinesysCodeUploadModal/GinesysCodeUploadModal";
import { createUpdatedDataPayload } from "utils/createUpdatedDataPayload";
import { custom } from "data/authData";

const allowedStudioReceivedStatuses = new Set([statusOptions.READY.key]);
// A bulk GRN image is only meaningful once the sample is physically in.
const allowedGrnUploadStatuses = new Set([statusOptions.RECEIVED.key]);
const blockedStudioEditStatuses = new Set([statusOptions.REJECTED.key]);
const inwardVisibleStatuses = [statusOptions.READY.key, statusOptions.RECEIVED.key, statusOptions.REJECTED.key];
const canUploadGrnForRow = (row) => allowedGrnUploadStatuses.has(row?.[sampleStatusColumn.key]);
const grnSourceTabs = {
	FILE: "file",
	CAMERA: "camera",
};
const GRN_PREVIEW_WIDTH = 220;
const GRN_PREVIEW_HEIGHT = 330;
const SAMPLE_DETAIL_PREVIEW_WIDTH = 140;
const SAMPLE_DETAIL_PREVIEW_HEIGHT = 200;

const grnPreviewFrameSx = (hasSrc, width = GRN_PREVIEW_WIDTH, height = GRN_PREVIEW_HEIGHT) => ({
	width,
	height,
	maxWidth: "100%",
	borderRadius: "12px",
	overflow: "hidden",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	bgcolor: grey[50],
	flexShrink: 0,
	border: hasSrc ? `1px solid ${grey[200]}` : `1.5px dashed ${grey[300]}`,
	boxShadow: "none",
});

const GrnPreviewBox = ({
	src,
	alt = "GRN preview",
	emptyText = "No image selected",
	width = GRN_PREVIEW_WIDTH,
	height = GRN_PREVIEW_HEIGHT,
}) => (
	<Paper elevation={0} sx={grnPreviewFrameSx(Boolean(src), width, height)}>
		{src ? (
			<img src={src} alt={alt} style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
		) : (
			<Box textAlign="center" color="text.secondary" px={2}>
				<ImageIcon sx={{ fontSize: height < 250 ? 32 : 40, color: grey[400], mb: 0.5 }} />
				<Typography variant="body2" color="text.secondary">
					{emptyText}
				</Typography>
			</Box>
		)}
	</Paper>
);

const getGrnModalTitle = ({ viewOnly, existingUrl, count }) => {
	if (viewOnly) return "GRN image";
	if (existingUrl) return count > 1 ? "Update bulk GRN" : "Update GRN image";
	return count > 1 ? "Upload bulk GRN" : "Upload GRN image";
};
const formatMySqlDateTime = (date = new Date()) => {
	const pad = (value) => String(value).padStart(2, "0");
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(
		date.getMinutes(),
	)}:${pad(date.getSeconds())}`;
};

const StudioSampleInward = () => {
	const { user } = AuthState();
	const { setLoadingOpen, setSnackBarOpen, setSnackBar, snackBar } = GlobalState();

	const disableEdit = user?.role?.[custom.SAMPLE_INWARD_VIEWER.key] === true;

	const [selectedSkus, setSelectedSkus] = useState([]);
	const [allSkus, setAllSkus] = useState([]);
	const [editModal, setEditModal] = useState(false);
	const [historyModal, setHistoryModal] = useState(false);
	const [originalData, setOriginalData] = useState(null);
	const [editedData, setEditedData] = useState(null);
	const [selectedHistorySku, setSelectedHistorySku] = useState(null);
	const [image, setImage] = useState(null);
	const [bulkEdit, setBulkEdit] = useState(false);
	const [cameraActive, setCameraActive] = useState(null);
	const [markAsReceived, setMarkAsReceived] = useState(false);
	const [markAsRejected, setMarkAsRejected] = useState(false);
	const [grnModal, setGrnModal] = useState(false);
	const [grnImage, setGrnImage] = useState(null);
	const [grnPreviewUrl, setGrnPreviewUrl] = useState(null);
	const [grnIcodes, setGrnIcodes] = useState([]);
	const [grnViewOnly, setGrnViewOnly] = useState(false);
	const [grnExistingUrl, setGrnExistingUrl] = useState(null);
	const [grnSourceTab, setGrnSourceTab] = useState(grnSourceTabs.FILE);
	const [ginesysUploadModal, setGinesysUploadModal] = useState(false);

	useEffect(() => {
		if (selectedSkus.length > 1) setBulkEdit(true);
		else setBulkEdit(false);
	}, [selectedSkus]);

	useEffect(() => {
		if (!grnImage) {
			setGrnPreviewUrl(null);
			return;
		}
		const previewUrl = URL.createObjectURL(grnImage);
		setGrnPreviewUrl(previewUrl);
		return () => URL.revokeObjectURL(previewUrl);
	}, [grnImage]);

	const selectedSkuRows = allSkus.filter((item) => selectedSkus.includes(item[shootSkuIcodeColumn.key]));
	const isBulkReceivedEligible =
		selectedSkus.length > 0 &&
		selectedSkuRows.length === selectedSkus.length &&
		selectedSkuRows.every((item) => allowedStudioReceivedStatuses.has(item?.[sampleStatusColumn.key]));
	const isSingleReceivedEligible = allowedStudioReceivedStatuses.has(editedData?.[sampleStatusColumn.key]);
	const canShowMarkAsReceived = bulkEdit ? isBulkReceivedEligible : isSingleReceivedEligible;
	const canShowRejectSample = canShowMarkAsReceived;
	const isGrnUploadEligible =
		selectedSkus.length > 0 &&
		selectedSkuRows.length === selectedSkus.length &&
		selectedSkuRows.every((item) => allowedGrnUploadStatuses.has(item?.[sampleStatusColumn.key]));

	const handleEditModalClose = () => {
		setEditModal(false);
		setEditedData(null);
		setOriginalData(null);
		setSelectedSkus([]);
		setImage(null);
		setMarkAsReceived(false);
		setMarkAsRejected(false);
	};

	const handleIcodeSelect = (data) => {
		if (blockedStudioEditStatuses.has(data?.[sampleStatusColumn.key])) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Rejected samples cannot be edited",
				severity: "warning",
			});
			return;
		}

		setEditedData(data);
		setOriginalData(data);
		setMarkAsReceived(false);
		setMarkAsRejected(false);
		setEditModal(true);
	};

	const handleStudioSelectionChange = (selectedIds) => {
		const nextSelectedIds = selectedIds.filter((id) => {
			const row = allSkus.find((item) => item[shootSkuIcodeColumn.key] === id);
			return !blockedStudioEditStatuses.has(row?.[sampleStatusColumn.key]);
		});

		if (nextSelectedIds.length !== selectedIds.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Rejected samples cannot be selected for bulk edit",
				severity: "warning",
			});
		}

		setSelectedSkus(nextSelectedIds);
	};

	const handleBulkEditButtonClick = () => {
		if (selectedSkus.length === 0) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Please select at least one SKU to edit",
				severity: "warning",
			});
			return;
		}
		setEditedData({});
		setOriginalData({});
		setImage(null);
		setMarkAsReceived(isBulkReceivedEligible);
		setMarkAsRejected(false);
		setEditModal(true);
	};
	const handleGrnButtonClick = () => {
		if (!isGrnUploadEligible) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: selectedSkus.length
					? "A bulk GRN image can only be uploaded for samples in RECEIVED status"
					: "Please select at least one received SKU",
				severity: "warning",
			});
			return;
		}
		setGrnIcodes(selectedSkus);
		setGrnViewOnly(false);
		setGrnExistingUrl(null);
		setGrnImage(null);
		setGrnSourceTab(grnSourceTabs.FILE);
		setGrnModal(true);
	};

	const handleGrnColumnAction = (row) => {
		const icode = row?.[shootSkuIcodeColumn.key];
		if (!icode) return;

		if (hasSampleGrnImage(row)) {
			setGrnIcodes([icode]);
			setGrnExistingUrl(row[shootStudioSampleGrnImgUrlCol.key]);
			setGrnViewOnly(disableEdit || !canUploadGrnForRow(row));
			setGrnImage(null);
			setGrnSourceTab(grnSourceTabs.FILE);
			setGrnModal(true);
			return;
		}

		if (disableEdit || !allowedGrnUploadStatuses.has(row?.[sampleStatusColumn.key])) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "A GRN image can only be uploaded for samples in RECEIVED status",
				severity: "warning",
			});
			return;
		}

		setGrnIcodes([icode]);
		setGrnViewOnly(false);
		setGrnExistingUrl(null);
		setGrnImage(null);
		setGrnSourceTab(grnSourceTabs.FILE);
		setGrnModal(true);
	};

	const handleGrnModalClose = () => {
		setGrnModal(false);
		setGrnImage(null);
		setGrnViewOnly(false);
		setGrnExistingUrl(null);
		setGrnIcodes([]);
		setGrnSourceTab(grnSourceTabs.FILE);
	};

	const handleGrnUpload = async () => {
		if (grnViewOnly) return;
		if (!grnIcodes.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Please select at least one received SKU",
				severity: "warning",
			});
			return;
		}
		if (!grnImage) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Please capture or choose a GRN image",
				severity: "warning",
			});
			return;
		}

		setLoadingOpen(true);
		try {
			const formData = new FormData();
			formData.append("grnImages", grnImage);

			const uploadRes = await axios.post("/api/studio/upload_sample_grn_images", formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});
			const grnImgUrl = uploadRes.data?.result?.[0]?.publicUrl;
			if (!grnImgUrl) throw new Error("GRN image upload failed");

			const res = await axios.put("/api/studio/bulk_upload_sample_grn", {
				icode_list: grnIcodes,
				grn_img_url: grnImgUrl,
			});

			setAllSkus((prev) =>
				prev.map((item) =>
					grnIcodes.includes(item[shootSkuIcodeColumn.key])
						? { ...item, [shootStudioSampleGrnImgUrlCol.key]: grnImgUrl, [shootStudioSampleGrnUploadedCol.key]: "YES" }
						: item,
				),
			);

			const mailFailedFor = res.data?.result?.mail_failed_for ?? [];
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: mailFailedFor.length
					? `GRN image saved, but the notification failed for: ${mailFailedFor.join(", ")}`
					: res.data?.message || "GRN image uploaded and merchants notified",
				severity: mailFailedFor.length ? "warning" : "success",
			});

			if (grnIcodes.length > 1) setSelectedSkus([]);
			handleGrnModalClose();
		} catch (err) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				severity: "error",
				message: parseErrorMessage(err),
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleDataSave = async (data) => {
		setLoadingOpen(true);
		let imagesArray = image ? [image] : [];
		try {
			if (markAsReceived && !canShowMarkAsReceived) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					open: true,
					message: "Only ready samples can be marked as received",
					severity: "error",
				});
				setLoadingOpen(false);
				return;
			}
			if (markAsRejected && !canShowRejectSample) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					open: true,
					message: "Only ready samples can be rejected",
					severity: "error",
				});
				setLoadingOpen(false);
				return;
			}

			const workingData = { ...(data || {}) };

			if (markAsReceived) {
				workingData[sampleStatusColumn.key] = statusOptions.RECEIVED.key;
				workingData[shootStudioSampleDetailsColumns.sample_inward_date.key] = formatMySqlDateTime();
			} else if (markAsRejected) {
				workingData[sampleStatusColumn.key] = statusOptions.REJECTED.key;
			}

			let payload = bulkEdit
				? createUpdatedDataPayload(
						workingData,
						{},
						{
							...shootStudioSampleDetailsColumns,
							[sampleStatusColumn.key]: sampleStatusColumn,
						},
					)
				: createUpdatedDataPayload(workingData, originalData, {
						...shootStudioSampleDetailsColumns,
						[sampleStatusColumn.key]: sampleStatusColumn,
					});

			if (imagesArray.length > 0) {
				const formData = new FormData();
				imagesArray.forEach((file) => {
					formData.append("sampleImages", file); // append each file
				});

				// Wait for upload to finish
				const res = await axios.post("/api/studio/upload_sample_images", formData, {
					headers: { "Content-Type": "multipart/form-data" },
				});

				if (!res.data?.result?.length) throw new Error("Image upload failed");

				// Set first image URL in payload (or handle multiple URLs if needed)
				payload[shootStudioSampleImgUrlCol.key] = res.data.result[0].publicUrl;
				workingData[shootStudioSampleImgUrlCol.key] = res.data.result[0].publicUrl;
			}
			if (Object.keys(payload).length === 0) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: "No changes made",
					severity: "warning",
				});
				setLoadingOpen(false);
				return;
			}
			if (bulkEdit) {
				await axios.put("/api/studio/bulk_update_shoot_studio_sample_details", {
					body: payload,
					icode_list: selectedSkus,
				});

				// Update local state for bulk
				setAllSkus((prev) =>
					prev.map((item) => (selectedSkus.includes(item[shootSkuIcodeColumn.key]) ? { ...item, ...payload } : item)),
				);
			} else {
				await axios.put(`/api/studio/update_shoot_studio_sample_details/${data[shootSkuIcodeColumn.key]}`, payload);

				// Update local state for single
				setAllSkus((prev) =>
					prev.map((item) =>
						item[shootSkuIcodeColumn.key] === data[shootSkuIcodeColumn.key] ? { ...item, ...payload } : item,
					),
				);
			}
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Data updated successfully",
				severity: "success",
			});

			setSelectedSkus([]);
			setCameraActive(false);
			handleEditModalClose();
		} catch (err) {
			setSnackBarOpen(true);

			setSnackBar({
				...snackBar,
				severity: "error",
				message: parseErrorMessage(err),
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleGinesysUploadSave = async (rows) => {
		setLoadingOpen(true);
		try {
			const response = await axios.put("/api/studio/bulk_upload_ginesys_codes", { rows });
			const result = response.data?.result || {};
			const updatedRows = result.updated_rows || [];
			if (updatedRows.length) {
				const updatedByCode = new Map(updatedRows.map((row) => [String(row.i_code).trim().toUpperCase(), row.ginesys_code]));
				setAllSkus((prev) =>
					prev.map((item) => {
						const nextCode = updatedByCode.get(String(item[shootSkuIcodeColumn.key] ?? "").trim().toUpperCase());
						return nextCode == null ? item : { ...item, ginesys_code: nextCode };
					}),
				);
			}
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: response.data?.message || "Ginesys codes updated",
				severity: result.missing_i_codes?.length ? "warning" : "success",
			});
			return response.data;
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				severity: "error",
				message: parseErrorMessage(error),
			});
			return { status: "error", message: parseErrorMessage(error) };
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleHistoryClick = (selectedRow) => {
		setSelectedHistorySku(selectedRow);
		setHistoryModal(true);
	};

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 64px - 72px)",
				display: "flex",
				flexDirection: "column",
				minHeight: 0,
				boxSizing: "border-box",
				px: "10px",
			}}>
			<Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
				<SkuSectionTanstack
					selectedSkus={selectedSkus}
					setSelectedSkus={setSelectedSkus}
					processCheckboxSelectionChange={handleStudioSelectionChange}
					shootData={{}}
					showGrnColumns={true}
					onGrnImageAction={handleGrnColumnAction}
					disableGrnUpload={disableEdit}
					canGrnUpload={canUploadGrnForRow}
					defaultFilterObj={{ [sampleStatusColumn.key]: inwardVisibleStatuses }}
					onRowClick={handleIcodeSelect}
					minHeight="100%"
					maxHeight="100%"
					skusState={allSkus}
					setSkusState={setAllSkus}
					hideSearchHeader={true}
					showActionColumn={true}
					actionList={[{ name: "History", onClick: handleHistoryClick }]}
					disableSelection={disableEdit}
					enableExcelExport={true}
					exportFileName="sample_sku_details.xlsx"
					childrenAboveTable={
						<Box flex={1} alignItems="flex-end" display="flex" gap={1}>
							<Button variant="contained" disabled={!bulkEdit} onClick={handleBulkEditButtonClick} size="small">
								Bulk Edit
							</Button>
							<Button
								variant="outlined"
								disabled={disableEdit}
								onClick={() => setGinesysUploadModal(true)}
								size="small"
								startIcon={<FileUploadOutlined fontSize="small" />}
								sx={{ textTransform: "none", fontWeight: 600 }}>
								Ginesys Code
							</Button>
							<Button
								variant="outlined"
								disabled={disableEdit || !isGrnUploadEligible}
								onClick={handleGrnButtonClick}
								size="small"
								startIcon={<CameraAltRounded fontSize="small" />}
								sx={{ textTransform: "none", borderRadius: 1.5, fontWeight: 600 }}>
								Upload GRN
							</Button>
						</Box>
					}
				/>
			</Box>

			<GinesysCodeUploadModal modal={ginesysUploadModal} setModal={setGinesysUploadModal} callbackFun={handleGinesysUploadSave} />

			<Dialog fullWidth maxWidth="lg" open={editModal} onClose={handleEditModalClose}>
				<Typography variant="h6" p={2} pb={1}>
					{bulkEdit ? "Bulk" : ""}Edit Merchant Sample Details
				</Typography>

				<Box sx={{ p: 2, pt: 1, borderBottom: "1px solid #ccc" }}>
					{bulkEdit ? (
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
							<Typography variant="h6" fontSize={16} sx={{ width: "100%" }} color={grey[700]}>
								Selected Icodes
							</Typography>
							{selectedSkus.map((sku) => (
								<Box
									key={sku}
									sx={{
										backgroundColor: grey[400],
										borderRadius: "5px",
										padding: "5px",
										display: "flex",
										alignItems: "center",
										gap: 1,
									}}>
									<Typography variant="subtitle1">{sku}</Typography>
								</Box>
							))}
						</Box>
					) : (
						<Typography variant="subtitle1">SKU ICODE: {editedData?.[shootSkuIcodeColumn.key]}</Typography>
					)}
				</Box>

				<Box sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 2 }}>
					{Object.values(shootStudioSampleDetailsColumns).map((column) => {
						return (
							<RenderInputField
								key={column.key}
								column={column}
								data={editedData}
								setData={setEditedData}
								selectOptObjName={selectOptionsKeyName}
								width="32%"
								longWidth="66%"
							/>
						);
					})}
				</Box>

				<Box sx={{ px: 2, pb: 1 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={markAsReceived}
								onChange={(e) => {
									const checked = e.target.checked;
									setMarkAsReceived(checked);
									if (checked) setMarkAsRejected(false);
								}}
								disabled={disableEdit || !canShowMarkAsReceived}
							/>
						}
						label="Mark as received"
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={markAsRejected}
								onChange={(e) => {
									const checked = e.target.checked;
									setMarkAsRejected(checked);
									if (checked) setMarkAsReceived(false);
								}}
								disabled={disableEdit || !canShowRejectSample}
							/>
						}
						label="Reject sample"
					/>
					{!canShowMarkAsReceived && (
						<Typography variant="caption" color="text.secondary">
							{bulkEdit
								? "Bulk receive and reject actions are enabled only when all selected SKUs are in READY status."
								: "Receive and reject actions are enabled only when status is READY."}
						</Typography>
					)}
				</Box>

				{/* IMAGE CAPTURE SECTION */}
				<Box sx={{ p: 2, pt: 0, display: "flex", flexWrap: "wrap", gap: 4 }}>
					<Stack spacing={1}>
						<Typography variant="h6" fontSize={18} color={grey[700]}>
							Sample Image (Optional)
						</Typography>
						{editedData?.[shootStudioSampleImgUrlCol.key] && !cameraActive ? (
							<Box display="flex" alignItems="center" columnGap={2}>
								<GrnPreviewBox
									src={editedData?.[shootStudioSampleImgUrlCol.key]}
									alt="Sample"
									emptyText="No sample image uploaded"
									width={SAMPLE_DETAIL_PREVIEW_WIDTH}
									height={SAMPLE_DETAIL_PREVIEW_HEIGHT}
								/>
								<Button variant="contained" onClick={() => setCameraActive(true)}>
									Update
								</Button>
							</Box>
						) : (
							<CameraCapture
								compact
								existingSrc={editedData?.[shootStudioSampleImgUrlCol.key]}
								previewWidth={SAMPLE_DETAIL_PREVIEW_WIDTH}
								previewHeight={SAMPLE_DETAIL_PREVIEW_HEIGHT}
								onCapture={(file) => {
									setImage(file);
								}}
							/>
						)}
					</Stack>
					{!bulkEdit && (
						<Stack spacing={1}>
							<Typography variant="h6" fontSize={18} color={grey[700]}>
								GRN Image
							</Typography>
							<GrnPreviewBox
								src={editedData?.[shootStudioSampleGrnImgUrlCol.key]}
								alt="GRN"
								emptyText="No GRN image uploaded"
								width={SAMPLE_DETAIL_PREVIEW_WIDTH}
								height={SAMPLE_DETAIL_PREVIEW_HEIGHT}
							/>
						</Stack>
					)}
				</Box>

				<DialogActions>
					<Button variant="outlined" onClick={handleEditModalClose}>
						Cancel
					</Button>
					<Button
						autoFocus
						variant="contained"
						sx={{ mr: 1 }}
						onClick={() => handleDataSave(editedData)}
						disabled={disableEdit}>
						Save
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={grnModal}
				onClose={handleGrnModalClose}
				PaperProps={{
					sx: {
						width: 580,
						maxWidth: "calc(100% - 32px)",
						maxHeight: "90vh",
						borderRadius: 2.5,
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
					},
				}}>
				<DialogTitle sx={{ px: 3, py: 2, display: "flex", alignItems: "flex-start", gap: 1 }}>
					<Box flex={1} minWidth={0}>
						<Typography variant="h6" fontWeight={600}>
							{getGrnModalTitle({
								viewOnly: grnViewOnly,
								existingUrl: grnExistingUrl,
								count: grnIcodes.length,
							})}
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
							{grnViewOnly
								? "Uploaded GRN for the SKU below."
								: "Warehouse verifies goods received from this photo. Merchants are notified after save."}
						</Typography>
					</Box>
					<IconButton onClick={handleGrnModalClose} sx={{ mt: -0.5 }}>
						<ClearRounded />
					</IconButton>
				</DialogTitle>

				<Divider />

				<DialogContent sx={{ px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 2.5 }}>
					<Box>
						<Typography variant="subtitle1" color={grey[700]} sx={{ mb: 1 }}>
							Selected SKUs ({grnIcodes.length})
						</Typography>
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, maxHeight: 80, overflow: "auto" }}>
							{grnIcodes.map((sku) => (
								<Chip key={sku} label={sku} variant="outlined" />
							))}
						</Box>
					</Box>

					{grnViewOnly ? (
						<Box sx={{ display: "flex", justifyContent: "center" }}>
							<GrnPreviewBox src={grnExistingUrl} alt="GRN" emptyText="No GRN image uploaded" />
						</Box>
					) : (
						<Stack spacing={2}>
							<ToggleButtonGroup
								exclusive
								fullWidth
								value={grnSourceTab}
								onChange={(_, value) => {
									if (!value) return;
									setGrnSourceTab(value);
									setGrnImage(null);
								}}
								sx={{
									p: 0.5,
									bgcolor: grey[100],
									borderRadius: 1.5,
									"& .MuiToggleButtonGroup-grouped": {
										border: 0,
										borderRadius: "8px !important",
									},
									"& .MuiToggleButton-root": {
										textTransform: "none",
										py: 1,
										fontWeight: 500,
										fontSize: 15,
										color: grey[600],
										gap: 1,
									},
									"& .Mui-selected": {
										bgcolor: "#fff !important",
										color: "#212121 !important",
										boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
									},
								}}>
								<ToggleButton value={grnSourceTabs.FILE}>
									<FileUploadOutlined />
									File
								</ToggleButton>
								<ToggleButton value={grnSourceTabs.CAMERA}>
									<CameraAltRounded />
									Camera
								</ToggleButton>
							</ToggleButtonGroup>

							{grnSourceTab === grnSourceTabs.FILE ? (
								<Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
									<GrnPreviewBox src={grnPreviewUrl || grnExistingUrl} emptyText="No image selected" />
									<Stack spacing={1.25} sx={{ minWidth: 160 }}>
										<Button
											variant="outlined"
											component="label"
											disableElevation
											startIcon={<FileUploadOutlined />}
											sx={{ textTransform: "none", borderRadius: 1.5, fontWeight: 600, px: 2.5 }}>
											{grnExistingUrl ? "Update" : "Select"}
											<input
												hidden
												type="file"
												accept="image/*"
												onChange={(e) => {
													const file = e.target.files?.[0];
													if (file) setGrnImage(file);
												}}
											/>
										</Button>
										<Typography variant="body2" color="text.secondary" sx={{ maxWidth: 180, wordBreak: "break-all" }}>
											{grnImage ? grnImage.name : "JPG or PNG"}
										</Typography>
									</Stack>
								</Box>
							) : (
								<CameraCapture
									compact
									existingSrc={grnExistingUrl}
									previewWidth={GRN_PREVIEW_WIDTH}
									previewHeight={GRN_PREVIEW_HEIGHT}
									onCapture={(file) => setGrnImage(file)}
								/>
							)}
						</Stack>
					)}
				</DialogContent>

				<Divider />

				<DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
					<Button variant="outlined" onClick={handleGrnModalClose} sx={{ textTransform: "none", borderRadius: 1.5, px: 2.5 }}>
						{grnViewOnly ? "Close" : "Cancel"}
					</Button>
					{!grnViewOnly && (
						<Button
							autoFocus
							variant="contained"
							disableElevation
							onClick={handleGrnUpload}
							disabled={disableEdit || !grnImage}
							sx={{ textTransform: "none", borderRadius: 1.5, px: 2.5, fontWeight: 600 }}>
							{grnExistingUrl ? "Update & Notify" : "Upload & Notify"}
						</Button>
					)}
				</DialogActions>
			</Dialog>

			<SampleHistoryModal skuDetails={selectedHistorySku} modal={historyModal} setModal={setHistoryModal} />
		</Box>
	);
};

export default StudioSampleInward;
